/**
 * Shared chat-cache invalidation for a received chat push.
 *
 * When a chat push arrives while the user is already viewing that conversation,
 * the banner is suppressed and the inline view must instead be refreshed. Both
 * push paths need the SAME trio of invalidations:
 *   - the legacy path, inside `useWebPushNotifications`' CHAT_MESSAGE_RECEIVED
 *     branch (root SW → postMessage), and
 *   - the OneSignal owner path, both from OneSignal's own `foregroundWillDisplay`
 *     suppression (`useOneSignalNotificationEvents`) and — during the dual-send
 *     overlap, while a device is still legacy-VAPID-subscribed — from the root
 *     SW's CHAT_MESSAGE_RECEIVED relay (`useSwChatMessageRefresh`).
 *
 * Centralizing the trio keeps every consumer in lockstep (the same silent-drift
 * risk the shared safe-path / SW-message modules exist to prevent): room
 * messages, DM user-messages, and the user's rooms list. Rejections are caught
 * and logged (sanitized) — a failed refresh must never surface to the caller.
 */
import type { QueryClient } from '@tanstack/react-query';
import { CACHE_KEYS } from '@config/reactQuery';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

/**
 * Invalidate the three chat caches that reflect an incoming chat message so a
 * suppressed / background push updates the active view inline. Never rejects.
 *
 * @param queryClient - the app's React Query client.
 * @param userId - the signed-in user's id (for the userRooms key); `''` when
 *   signed out, matching the legacy branch's `userId ?? ''`.
 */
export function invalidateChatCaches(
  queryClient: QueryClient,
  userId: string | undefined,
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: CACHE_KEYS.roomMessagesBase }),
    queryClient.invalidateQueries({ queryKey: CACHE_KEYS.userMessagesBase }),
    queryClient.invalidateQueries({ queryKey: CACHE_KEYS.userRooms(userId ?? '') }),
  ])
    .then(() => undefined)
    .catch((error: unknown) => {
      logger.error('Chat push: failed to invalidate chat caches', sanitizeError(error));
    });
}
